import { StyleRoot } from "@redotech/react-util/style";
import { WidgetAutocheck } from "@redotech/redo-model/widget";
import StarIcon from "@redotech/redo-web/arbiter-icon/misc_star_icon.svg";
import { ReviewsRating } from "@redotech/redo-web/five-star-row/reviews-rating";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { useContext } from "react";
import { createRoot } from "react-dom/client";
import { RedoShopifyRpcClientContextProvider } from "../client/redo-shopify-rpc-client";
import { log } from "../monitoring/log";
import { RedoRoot } from "../root";
import STYLE_MANAGER from "../style-manager";
import { StarRatingsContext, StarRatingsProvider } from "./context";

export function initialize(autocheck: WidgetAutocheck) {
  log("Initializing Review Stars");

  const elements = document.querySelectorAll("div.redo-review-stars");

  log(`Found ${elements.length} review stars containers`);

  if (elements.length === 0) {
    return;
  }

  const validElements: Array<{ element: Element; productId: string }> = [];
  elements.forEach((element) => {
    const productId = element.getAttribute("product_id");
    if (!productId) {
      log("Skipping element without product_id");
      return;
    }

    // Shopify applies display: none to empty divs
    (element as HTMLElement).style.display = "unset";

    validElements.push({ element, productId });
  });

  if (validElements.length === 0) {
    return;
  }

  const storeUrl =
    window.redoStorefront?.storeUrl ??
    window.Shopify?.shop ??
    window.Shopify?.Checkout?.apiHost;

  const baseUrl =
    window.redoStorefront?.env?.REDO_SHOPIFY_SERVER_URL ??
    process.env.REDO_SHOPIFY_SERVER_URL!;

  validElements.forEach(({ element, productId }) => {
    const root = createRoot(element);
    root.render(
      <StyleRoot container={element} styleManager={STYLE_MANAGER}>
        <RedoRoot extensionShopifyClient={undefined} storeUrl={storeUrl}>
          <RedoShopifyRpcClientContextProvider
            baseUrl={baseUrl}
            storeUrl={window.Shopify?.shop ?? window.Shopify?.Checkout?.apiHost}
            widgetId={window.redoWidgetId}
          >
            <StarRatingsProvider
              productIds={validElements.map((item) => item.productId)}
            >
              <RedoReviewStars productId={productId} />
            </StarRatingsProvider>
          </RedoShopifyRpcClientContextProvider>
        </RedoRoot>
      </StyleRoot>,
    );
  });
}

function RedoReviewStars({ productId }: { productId: string }) {
  const ratings = useContext(StarRatingsContext);
  const rating = ratings?.[productId] ?? null;

  if (!ratings) {
    return <Text fontSize="sm">Loading ratings...</Text>;
  }

  return (
    <Flex align="center" dir="row" gap="md" py="sm">
      <ReviewsRating
        editable={false}
        Icon={StarIcon}
        iconSize={18}
        rating={rating ? rating.rating : 0}
      />
      <Text fontSize="sm">{`(${rating ? rating.numReviews : 0})`}</Text>
    </Flex>
  );
}
